import { AnimatePresence, motion } from "framer-motion";
import { IoCloseSharp } from "react-icons/io5";
import styled from "styled-components";
import { IMovieModalProps } from "../types";
import { makeImagePath } from "../utils";

const Overlay = styled(motion.div)`
  position: fixed;
  z-index: 5001;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  opacity: 0;
`;

const MovieModalWrapper = styled(motion.div)`
  position: fixed;
  z-index: 5002;
  background-color: #181818;
  width: 900px;
  height: 76vh;
  left: 0;
  right: 0;
  top: 17vh;
  margin: 0 auto;
  border-radius: 5px;
`;

const ModalBanner = styled.div<{ bgPhoto: string }>`
  display: relative;
  border-radius: 5px;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-image: linear-gradient(0deg, #181818, transparent 50%),
    url(${(props) => props.bgPhoto});
`;

const ModalBannerDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const ModalBannerTopRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const ModalCloseBtn = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: #181818;
  border: 0;
  margin: 10px;
  height: 36px;
  width: 36px;
  &:hover {
    cursor: pointer;
  }
`;

const ModalBannerBottomRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ModalTitle = styled.div`
  font-size: 30px;
  font-family: "Unbounded", cursive;
  padding: 30px 30px;
`;

const ModalDetails = styled.div`
  display: grid;
  grid-template-columns: 0.67fr 0.33fr;
`;

const ModalDetailsMainCol = styled.div``;

const ModalInfo = styled.div`
  padding: 20px 30px;
  display: flex;
`;

const ModalDescription = styled.div`
  padding: 5px 30px;
  font-size: 1.7vh;
`;

const ModalDetailsSubCol = styled.div`
  padding-top: 20px;
`;

const ModalDetail = styled.div`
  padding: 0 30px 15px 0;
`;

function MovieModal({
  movieMatch,
  onOverlayClick,
  selectedMovieId,
  data,
  isLoading,
}: IMovieModalProps) {
  return (
    <AnimatePresence>
      {movieMatch ? (
        <>
          <Overlay
            onClick={onOverlayClick}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          />
          <MovieModalWrapper layoutId={selectedMovieId}>
            {!isLoading && data ? (
              <>
                <div style={{ height: "66%" }}>
                  <ModalBanner bgPhoto={makeImagePath(data.backdrop_path)}>
                    <ModalBannerDetails>
                      <ModalBannerTopRow>
                        <ModalCloseBtn onClick={onOverlayClick}>
                          <IoCloseSharp color="white" size={38} />
                        </ModalCloseBtn>
                      </ModalBannerTopRow>
                      <ModalBannerBottomRow>
                        <ModalTitle>{data.title}</ModalTitle>
                      </ModalBannerBottomRow>
                    </ModalBannerDetails>
                  </ModalBanner>
                </div>
                <ModalDetails>
                  <ModalDetailsMainCol>
                    <ModalInfo>
                      <span
                        style={{
                          fontSize: "1.7vh",
                          color: "#47d369",
                          fontWeight: 600,
                          paddingRight: "10px",
                        }}
                      >
                        {Math.round(data.vote_average * 10)}% Match
                      </span>
                      <span
                        style={{
                          fontSize: "1.7vh",
                          fontWeight: 600,
                          paddingRight: "10px",
                        }}
                      >
                        {data.release_date.split("-")[0]}
                      </span>
                      <span
                        style={{
                          fontSize: "1.7vh",
                          fontWeight: 600,
                          paddingRight: "10px",
                        }}
                      >
                        {data.runtime > 60
                          ? `${Math.floor(data.runtime / 60)}h ${
                              data.runtime - Math.floor(data.runtime / 60) * 60
                            }m`
                          : `${data.runtime}m`}
                      </span>
                    </ModalInfo>
                    <ModalDescription>{data.overview}</ModalDescription>
                  </ModalDetailsMainCol>
                  <ModalDetailsSubCol>
                    <ModalDetail>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "5px",
                          color: "#777777",
                        }}
                      >
                        Status:
                      </span>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "10px",
                        }}
                      >
                        {data.status}
                      </span>
                    </ModalDetail>
                    <ModalDetail>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "5px",
                          color: "#777777",
                        }}
                      >
                        Genres:
                      </span>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "10px",
                        }}
                      >
                        {data.genres.map((genre) => genre.name).join(" · ")}
                      </span>
                    </ModalDetail>
                    <ModalDetail>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "5px",
                          color: "#777777",
                        }}
                      >
                        Production Countries:
                      </span>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "10px",
                        }}
                      >
                        {data.production_countries
                          .map((country) => country.name)
                          .join(" · ")}
                      </span>
                    </ModalDetail>
                    <ModalDetail>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "5px",
                          color: "#777777",
                        }}
                      >
                        Production Companies:
                      </span>
                      <span
                        style={{
                          fontSize: "1.5vh",
                          paddingRight: "10px",
                        }}
                      >
                        {data.production_companies
                          .map((company) => company.name)
                          .join(" · ")}
                      </span>
                    </ModalDetail>
                  </ModalDetailsSubCol>
                </ModalDetails>
              </>
            ) : null}
          </MovieModalWrapper>
        </>
      ) : null}
    </AnimatePresence>
  );
}

export default MovieModal;
